import { Injectable, signal } from "@angular/core";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { BehaviorSubject, catchError, from, map, Observable, of, switchMap, throwError } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { WalletService } from "../pages/account/account-wallet/wallet.services";
import { StoreRepositoryService } from "../pages/store/store-repository.service";
import { PaymentService } from "./payment.service";

@Injectable({
  providedIn: "root",
})
export class CashPurchaseService {
  private purchaseDetail = {} as Data;
  private responseObject: Data;
  private storedCards;
  private selectedCardData: string;
  private storedCardsSubject = new BehaviorSubject<Data>([]);
  purchaseDetails$ = new BehaviorSubject<Data | null>(null);
  storedCards$ = this.storedCardsSubject.asObservable();
  toastTrigger$ = new BehaviorSubject<boolean>(false);
  public selectedDiscountValueSignal = signal<number>(0);
  private readonly payment_response_description = new Set<string>([
    "Approved",
    "Aprobado",
    "Approuvé",
    "Approvato",
    "Genehmigt",
    "Aprovado",
    "Transaktion erfolgreich",
    "Transacción completada con éxito",
    "Transaction réussie",
    "Transazione riuscita",
    "Transação bem-sucedida",
    "Transaction succeeded",
  ]);

  constructor(
    private paymentService: PaymentService,
    private authService: AuthService,
    private firestore: Firestore,
    private walletService: WalletService,
    private storeService: StoreRepositoryService,
  ) {
    this.getStoreCard();
  }

  // This function makes the request to initiate the customer purchase.
  public proceedToPay(offer_id): Observable<Data> {
    this.purchaseDetail = {};
    this.purchaseDetails$.next(null);
    return this.purchaseDetails(offer_id).pipe(
      switchMap((purchaseDetail) =>
        from(
          this.storeService.validateOfferCappingBeforePurchase(
            purchaseDetail,
            "cash_purchase_service_pre_payment"
          )
        ).pipe(
          switchMap((isOfferCappingBlocked) => {
            if (isOfferCappingBlocked) {
              return of({
                status: "rejected",
                errorCode: "OFFER_CAP_REACHED",
                purchaseDetail,
              });
            }

            const finalPurchaseDetails = {
              ...purchaseDetail,
              voucher_discount: this.selectedDiscountValueSignal(),
            };

            return this.insertCustomerPurchase(finalPurchaseDetails);
          })
        )
      ),
      switchMap((result) => {
        if (result?.errorCode === "OFFER_CAP_REACHED") {
          return of(result);
        }

        if (result?.response?.status === "rejected") return of(result);
        if (result?.response?.status !== "SUCCESS") {
          return of({ errorCode: "1051" });
        }

        const customerPurchaseId = result?.response?.customer_purchase_id;
        const encryptedCustomerPurchaseId = result?.response?.encrypted_customer_purchase_id;
        this.paymentService.cashPurchaseCustomerPurchaseId$.next(encryptedCustomerPurchaseId);

        return this.processPayment(customerPurchaseId).pipe(
          switchMap((processPayResult) => {
            console.info("processPayResult", processPayResult);
            if (!processPayResult) return of({ errorCode: "1050" });
            if (processPayResult?.response?.status == "rejected") return of(processPayResult);

            if (processPayResult?.response?.status === "SUCCESS") {
              this.paymentService.emitSyndicatePurchaseComplete();
              this.responseObject = {
                ...this.purchaseDetail,
                ...processPayResult?.response,
                p_type: "10",
              } as Data;
              let myuuid = uuidv4();
              const paymentDocId = String(myuuid) || "";

              this.updatePaymentDoc(customerPurchaseId, paymentDocId);

              if (processPayResult.response?.syndicate_id) {
                this.paymentService.updateSyndicateRequest(
                  this.responseObject,
                  processPayResult.response?.syndicate_id
                );
              }
              return of({
                status: "SUCCESS",
                encryptedCustomerPurchaseId: encryptedCustomerPurchaseId,
                purchaseDetail: this.purchaseDetail,
              });
            } else return of({ errorCode: "1050" });
          })
        );
      }),
      catchError((err) => of(err))
    );
  }

  // Gather purchase details from offer id
  private purchaseDetails(slide_to_buy: string | number | { offer_id?: string; prosub_id?: string }) {
    // Some times slide_to_buy is passed as a string or number (offer_id), other times as an object with offer_id or prosub_id
    const offerDetails$ =
      typeof slide_to_buy === "string" || typeof slide_to_buy === "number"
        ? this.getOfferDetails(String(slide_to_buy))
        : slide_to_buy?.offer_id
          ? this.getOfferDetails(slide_to_buy?.offer_id)
          : slide_to_buy?.prosub_id
            ? this.getProsubDetails(slide_to_buy?.prosub_id)
            : throwError(() => new Error("Missing offer_id or prosub_id"));

    return offerDetails$.pipe(
      map((offerData) => {
        this.purchaseDetail.offers = offerData;
        this.purchaseDetail.page = "store";
        this.purchaseDetail.checkout = true;
        this.purchaseDetail.offer_id = localStorage.getItem("offer_id") ?? offerData?.offer_id;
        this.purchaseDetail.prosub_id = localStorage.getItem("prosub_id") ?? offerData?.prosub_id;
        this.purchaseDetail.tp1 = localStorage.getItem("TP1") ? localStorage.getItem("TP1") : "";
        this.purchaseDetail.tp2 = localStorage.getItem("TP2") ? localStorage.getItem("TP2") : "";
        this.purchaseDetail.tp3 = localStorage.getItem("TP3") ? localStorage.getItem("TP3") : "";
        this.purchaseDetail.t_id = localStorage.getItem("T_ID") ? localStorage.getItem("T_ID") : "";
        this.purchaseDetail.upsell_id = localStorage.getItem("upsell_ID") ? localStorage.getItem("upsell_ID") : "";
        this.purchaseDetail.other = localStorage.getItem("Other") ? localStorage.getItem("Other") : "";
        this.purchaseDetail.paymentOfferID = localStorage.getItem("paymentOfferID")
          ? localStorage.getItem("paymentOfferID")
          : "";
        if (!this.purchaseDetail?.line) {
          this.purchaseDetail.line = offerData?.lines;
        }
        if (!this.purchaseDetail?.real_name) {
          this.purchaseDetail.real_name = offerData?.lottery;
        }
        if (!this.purchaseDetail?.p_type) {
          this.purchaseDetail.p_type = offerData?.p_type;
        }
        if (!this.purchaseDetail?.selectedDay) {
          this.purchaseDetail.selectedDay = offerData?.draw_day;
        }
        if (!this.purchaseDetail?.selectedDiscount) {
          this.storeService.storeOffers$.subscribe((data) => {
            // Add defensive null checks to prevent "Cannot convert undefined or null to object" error
            // Check if data exists, cash_offer_discount exists, is an array, and has at least one element
            const hasValidDiscount = data &&
              data.cash_offer_discount &&
              Array.isArray(data.cash_offer_discount) &&
              data.cash_offer_discount.length > 0 &&
              data.cash_offer_discount[0];

            const purchaseDetails = {
              ...this.purchaseDetail,
              selectedDiscount: hasValidDiscount ? Object.assign({}, data.cash_offer_discount[0]) : {},
            };

            this.purchaseDetail = purchaseDetails;
          });
        }

        return { ...this.purchaseDetail, ...this.getCheckCardDetailId() };
      })
    );
  }

  // Update the payment collection upon completion
  private updatePaymentDoc(customerPurchaseId, paymentDocId) {
    const path_id = "At4Vyy58QQ2vknYAhw7T";
    const doc_data = { payment_response: this.responseObject, customer_purchase_id: customerPurchaseId };
    const dv = { docId: paymentDocId };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    }).subscribe({
      error: (err) => {
        console.error('Error in updatePaymentDoc:', err);
        throwError(() => err);
      }
    });
  }


  // Insert the customer purchase
  private insertCustomerPurchase(purchaseDetail) {
    return this.paymentService.insertCustomerPurchase(
      purchaseDetail,
      "/api_post_request/insert_customer_purchase_store_payment",
      true
    );
  }

  // Process the payment with purchase id.
  private processPayment(customer_purchase_id) {
    console.info("processPayment", customer_purchase_id);
    return this.paymentService.processCardPayment(
      customer_purchase_id,
      this.getCheckCardDetailId(),
      "/api_post_request/process_store_payment_existing_card"
    );
  }

  // Get offer details
  private getOfferDetails(offer_id: string): Observable<Data> {
    return this.authService.getOfferDetail(offer_id);
  }

  // This function returns the ID of the card that is currently checked by the user.
  private getCheckCardDetailId() {
    if (!this.storedCards) return;
    return this.storedCards?.find((x) => x?.id === this.selectedCardData);
  }

  // This function fetch the users stored cards.
  private getStoreCard() {
    try {
      this.walletService.paymentDetails$.subscribe((data) => {
        this.storedCardsSubject.next(data ?? []);
        this.storedCards = data;

        if (this.storedCards && this.storedCards.length > 0) {
          let withPriority = this.storedCards.filter(card => card.hasOwnProperty("priority"));
          let withoutPriority = this.storedCards.filter(card => !card.hasOwnProperty("priority"));
          withPriority.sort((a, b) => b.priority - a.priority);
          this.storedCards = [...withPriority, ...withoutPriority];

          const checkedCard = this.storedCards.find(card => card?.isChecked === true);
          if (checkedCard) {
            this.selectedCardData = checkedCard.id;
          } else {
            this.storedCards[0].isChecked = true;
            this.selectedCardData = this.storedCards[0].id;
          }
        }

      });
    } catch (error) {
      // Ensure storedCards is initialized as empty array on error to prevent null propagation
      this.storedCards = [];
      this.storedCardsSubject.next([]);
    }
  }

  isPaymentApproved(description: string) {
    return this.payment_response_description.has(description)
  }

  private getProsubDetails(prosub_id): Observable<Data> {
    const docRef = doc(this.firestore, `/general_modules/prosub_id/${prosub_id}/details`);

    return from(getDoc(docRef)).pipe(
      map((details) => {
        return details.exists() ? details.data() : {};
      }),
      catchError(() => of({}))
    );
  }

  // Replace all undefined values in an object with null
  // There was an issue where Firestore does not accept undefined values so we need to convert them to null
  private replaceUndefinedWithNull(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(v => this.replaceUndefinedWithNull(v));
    } else if (obj !== null && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, this.replaceUndefinedWithNull(v === undefined ? null : v)])
      );
    }
    return obj;
  }
}
